import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { formatDisplayDate } from '@/lib/utils';
import {
  Search,
  Plus,
  Filter,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Users,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Star,
  CreditCard,
  XCircle,
  CheckCircle,
  Ban,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import DataTablePagination from '@/components/ui/data-table-pagination';
import { useRiders, useDeleteRider, useToggleRiderActiveStatus } from '@/api-services/hooks/useRider';
import { PagedData, RiderDTO } from '@/api-services/hooks/adminTypes';
import DeleteRiderConfirmation from '@/components/riders/DeleteRiderConfirmation';

const RidersPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');

  // Pagination state (0-indexed for API)
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Delete functionality state
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [riderToDelete, setRiderToDelete] = useState<{ id: number; name: string; email?: string } | null>(null);

  // Delete rider hook
  const { mutate: deleteRider, isPending: isDeleting } = useDeleteRider({
    onSuccess: async (data, variables) => {
      toast.success('Rider deleted successfully! Corresponding user account has also been removed.');
      // Invalidate all riders queries to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['riders'] });
    },
    onError: (error, variables) => {
      toast.error(`Failed to delete rider: ${error.message}`);
      refetch(); // Refresh the riders list on error to restore correct state
    },
  });

  // Toggle active status hook
  const { mutate: toggleActiveStatus, isPending: isTogglingStatus } = useToggleRiderActiveStatus({
    onSuccess: async (data, riderId) => {
      toast.success("Rider status updated successfully!");
      // Update only the changed row in the cache instead of refetching entire table
      queryClient.setQueriesData(
        { queryKey: ['riders'] },
        (oldData: any) => {
          if (!oldData?.content) return oldData;

          const updatedContent = oldData.content.map((rider: RiderDTO) =>
            rider.riderId === riderId
              ? { ...rider, isActive: !rider.isActive }
              : rider
          );

          return {
            ...oldData,
            content: updatedContent,
          };
        }
      );
    },
    onError: (error, riderId) => {
      toast.error(`Failed to update rider status: ${error.message}`);
    },
  });

  const handleToggleStatus = (riderId: number) => {
    toggleActiveStatus(riderId);
  };

  // Fetch riders from API
  const { data, isLoading: loading, error, refetch } = useRiders(
    {
      page: currentPage,
      size: itemsPerPage,
      ...(searchTerm && { search: searchTerm }),
    },
    {
      onError: (err) => {
        toast.error(`Failed to load riders: ${err.message}`);
      },
    }
  );

  // Riders data from API - using React Query cache directly

  const getStatusBadge = (status: boolean) => {
    return status ? { variant: 'default', className: 'bg-success text-success-foreground' } : { variant: 'secondary', className: 'bg-muted text-muted-foreground' };
  };

  const handleRiderAction = (action: string, riderId: number, riderName: string) => {
    if (action === 'Edit Details') {
      navigate(`/admin/riders/edit/${riderId}`);
    } else {
      toast.success(`${action} action completed for ${riderName}`);
    }
  };

  // Delete handler functions
  const handleDeleteClick = (riderId: number, riderName: string, riderEmail?: string) => {
    setRiderToDelete({ id: riderId, name: riderName, email: riderEmail });
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!riderToDelete) return;

    deleteRider({
      riderId: riderToDelete.id,
      email: riderToDelete.email,
    });
    setDeleteConfirmationOpen(false);
    setRiderToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmationOpen(false);
    setRiderToDelete(null);
  };

  // Filter riders by status on the client side if needed
  const riders = useMemo(() => {
    const riderData = data as PagedData<RiderDTO> | undefined;
    if (!riderData || !riderData.content) return [];
    let filtered = riderData.content;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(rider => {
        const riderStatus = rider.status?.toLowerCase() || 'active';
        return riderStatus === statusFilter.toLowerCase();
      });
    }

    // Filter by active status
    if (activeFilter !== "all") {
      filtered = filtered.filter((rider) => {
        const isActive = rider.isActive ?? true;
        return activeFilter === "active" ? isActive : !isActive;
      });
    }

    return filtered;
  }, [data, statusFilter, activeFilter]);

  // Pagination calculations from API response
  const riderData = data as PagedData<RiderDTO> | undefined;
  const totalItems = riderData?.page?.totalElements ?? 0;
  const totalPages = riderData?.page?.totalPages ?? 0;

  const handlePageChange = (page: number) => {
    // Convert from 1-indexed (UI) to 0-indexed (API)
    setCurrentPage(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0); // Reset to first page when items per page changes
  };

  // Convert rider DTO to display format
  const getRiderDisplayName = (rider: RiderDTO) => {
    return rider.fullName || rider.emailId || 'Unknown Rider';
  };

  const getRiderEmail = (rider: RiderDTO) => {
    return rider.emailId || 'N/A';
  };

  const getRiderPhone = (rider: RiderDTO) => {
    return rider.phoneNumber || 'N/A';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading riders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Error Loading Riders</h3>
            <p className="text-muted-foreground mb-4">{error.message}</p>
            <Button onClick={() => refetch()}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Rider Management</h1>
          <p className="text-muted-foreground mt-1">Manage and monitor your rider base</p>
        </div>
        <Button
          className="bg-gradient-primary hover:opacity-90"
          onClick={() => navigate('/admin/riders/add')}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Rider
        </Button>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search riders by name or email..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-40">
                  <Filter className="w-4 h-4 mr-2" />
                  {statusFilter === 'all' ? 'All Status' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setStatusFilter('all')}>All Status</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('active')}>Active</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('inactive')}>Inactive</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('suspended')}>Suspended</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('pending')}>Pending</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-[140px]">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {activeFilter === "all"
                    ? "Active"
                    : activeFilter === "active"
                      ? "Active Only"
                      : "Inactive Only"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => setActiveFilter("all")}>
                  All
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setActiveFilter("active")}>
                  Active Only
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setActiveFilter("inactive")}>
                  Inactive Only
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {/* Riders Table */}
      <Card>
        <CardHeader>
          <CardTitle>Riders ({totalItems})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rider</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Wallet Balance</TableHead>
                  <TableHead>Payment Method</TableHead>
                  <TableHead>Signup Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {riders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No riders found
                    </TableCell>
                  </TableRow>
                ) : (
                  riders.map((rider) => {
                    const status = rider.isActive;
                    const statusBadge = getStatusBadge(status as boolean);
                    const displayName = getRiderDisplayName(rider as RiderDTO);
                    const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
                    return (
                      <TableRow key={rider.riderId} className="hover:bg-muted/50">
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <Avatar>
                              <AvatarFallback>{initials}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{displayName}</p>
                              <p className="text-sm text-muted-foreground">ID: {rider.riderId}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center text-sm">
                              <Mail className="w-3 h-3 mr-1 text-muted-foreground" />
                              {getRiderEmail(rider)}
                            </div>
                            <div className="flex items-center text-sm">
                              <Phone className="w-3 h-3 mr-1 text-muted-foreground" />
                              {getRiderPhone(rider)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusBadge.className}>
                            {status && <CheckCircle className="w-3 h-3 mr-1" />}
                            {!status && <Ban className="w-3 h-3 mr-1" />}
                            {status ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            <CreditCard className="w-4 h-4 text-chart-secondary mr-1" />
                            ${rider.walletBalance?.toLocaleString() || 0}
                          </div>
                        </TableCell>
                        <TableCell>
                          {rider.preferredPaymentMethod || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {rider.signupDate ? formatDisplayDate(rider.signupDate) : 'N/A'}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleRiderAction('Edit Details', rider.riderId || 0, displayName)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleToggleStatus(rider.riderId!)}
                                disabled={isDeleting || isTogglingStatus}
                              >
                                {rider.isActive ? (
                                  <>
                                    <ToggleLeft className="w-4 h-4 mr-2" />
                                    Deactivate
                                  </>
                                ) : (
                                  <>
                                    <ToggleRight className="w-4 h-4 mr-2" />
                                    Activate
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDeleteClick(rider.riderId || 0, displayName, getRiderEmail(rider as RiderDTO))}
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Rider
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {riders.length > 0 && totalPages > 0 && (
              <DataTablePagination
                currentPage={currentPage + 1} // Convert from 0-indexed to 1-indexed for UI
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                className="mt-4"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Rider Confirmation Modal */}
      <DeleteRiderConfirmation
        open={deleteConfirmationOpen}
        onOpenChange={handleDeleteCancel}
        riderName={riderToDelete?.name || ''}
        riderId={riderToDelete?.id || 0}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default RidersPage;